<template>
  <component
    :is="tag"
    class="button"
    :type="type"
    :class="classes"
    :disabled="isDisabled"
    :href="href"
    @click="handleClick"
  >
    <span v-if="loading" class="loading"><i class="icon icon-spinner" /></span>
    <slot name="icon" />
    <span class="text"><slot /></span>
  </component>
</template>

<script>
export default {
  name: "CustomButton",
  props: {
    tag: {
      type: String,
      default: "button",
      validator(value) {
        return ["a", "button"].includes(value);
      },
    },
    size: {
      type: String,
      default: "medium",
      validator(value) {
        return ["small", "medium", "large"].includes(value);
      },
    },
    width: {
      type: String,
      default: "auto",
      validator(value) {
        return ["narrow", "auto", "full"].includes(value);
      },
    },
    theme: {
      type: String,
      default: "primary",
      validator(value) {
        return ["primary", "secondary", "white"].includes(value);
      },
    },
    type: {
      type: String,
      default: "button",
      validator(value) {
        return ["button", "submit"].includes(value);
      },
    },
    form: {
      type: String,
      default: "fill",
      validator(value) {
        return [
          "fill",
          "outline",
          "transparent",
          "underline",
          "underline-invert",
        ].includes(value);
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    easysize: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click"],
  computed: {
    classes() {
      return {
        "button--large": this.size === "large",
        "button--small": this.size === "small",
        "button--primary": this.theme === "primary",
        "button--secondary": this.theme === "secondary",
        "button--white": this.theme === "white",
        "button--outline": this.form === "outline",
        "button--transparent": this.form === "transparent",
        "button--underline": this.form === "underline",
        "button--underline-invert": this.form === "underline-invert",
        "button--full": this.width === "full",
        "button--narrow": this.width === "narrow",
        "button--loading": this.loading,
        easysize_button: this.easysize,
      };
    },
    isDisabled() {
      return this.disabled || this.loading;
    },
  },
  methods: {
    handleClick() {
      this.$emit("click");
    },
  },
};
</script>

<style lang="scss" scoped>
.button {
  @include utility-label(13px);
  padding: 9px 20px;
  color: $color-white;
  outline: none;
  border: none;
  border-radius: 8px;
  border: 1px solid transparent;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }

  &--large {
    padding: 15px 28px;
  }

  &--small {
    padding: 6px 12px;
  }

  &--primary {
    border-color: $color-black;
    background-color: $color-black;
    @include utility-button(13px);
  }

  &--secondary,
  &:disabled {
    border-color: $color-grey-light;
    background-color: $color-grey-light;
    color: $color-grey-dark;
    @include utility-button(13px);
    cursor: default;
  }

  &--white {
    border-color: $color-white;
    background-color: $color-white;
    color: $color-black;
    @include utility-button(13px);

    &.button--outline {
      background-color: transparent;
      color: $color-white;
    }
  }

  &--outline {
    background-color: transparent;
    @include utility-label(13px);
    color: $color-black;
  }

  &--full {
    display: block;
    width: 100%;

    .text {
      display: block;
      text-align: center;
      width: 100% !important;
    }

    &.button--underline,
    &.button--underline-invert {
      .text {
        display: inline-block;
        width: auto !important;
        max-width: 100%;
      }
    }
  }

  &--narrow {
    padding: 9px;
  }

  &--transparent {
    background: transparent;
    border: none;
    padding-left: 0;
    padding-right: 0;
    color: $color-black;
    @include utility-label(13px);

    &:disabled {
      background-color: transparent;
    }
  }

  &--underline,
  &--underline-invert {
    padding: 0;
    color: $color-black;
    background-color: transparent;
    outline: none;
    border: none;
    @include utility-label(13px);

    .text {
      position: relative;
      display: inline-block;
      max-width: 100%;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        width: 100%;
        height: 1px;
        background: $color-black;
        bottom: -6px;
      }
    }

    &.button--secondary {
      color: $color-grey-dark;
    }

    &.button--white {
      color: $color-white;

      .text::after {
        background: $color-white;
      }
    }

    &.button--tertiary {
      @include editorial-body(16px);
      text-transform: none;
    }
  }

  &--underline-invert {
    .text::after {
      width: 0;
      transition: none;
    }

    &:hover .text::after,
    &:active .text::after {
      width: 100%;
      transition: width 0.5s ease-out;
    }
  }

  &.easysize_button {
    &:before {
      display: none;
    }
  }
}

.button--loading {
  position: relative;
  cursor: default;

  .loading {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 20px;
  }

  .text {
    opacity: 0.35;
  }
}

.icon.icon-spinner {
  transform-origin: center;
  display: inline-block;
  animation: spinner 0.75s infinite linear;
}

@keyframes spinner {
  100% {
    transform: rotate(360deg);
  }
}
</style>
